<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import { useSettingsStore } from "@/stores/storeSettings";

const storeSettings = useSettingsStore();

const props = defineProps({
	letters: {
		type: Array,
		required: true,
	},
});

const emit = defineEmits(["wordSelected", "wordChanging"]);

// const letterSize = 56;
const letterSize = computed(() => storeSettings.widthWindow && storeSettings.widthWindow <= 576 ? 34 : 56);
const minSpacing = 8;

// Динамический радиус в зависимости от количества букв
const radius = computed(() => {
	const n = props.letters.length;
	if (n <= 1) return 0;
	// Минимальный радиус чтобы буквы не наслаивались
	const minRadius = (letterSize.value + minSpacing) / (2 * Math.sin(Math.PI / n));
	return Math.max(minRadius, 80);
});

const size = computed(() => {
	return radius.value * 2 + letterSize.value + 20;
});

const center = computed(() => size.value / 2);

const container = ref(null);
const isDragging = ref(false);
const selectedIndices = ref([]);
const currentPos = ref(null);
const lastExitedIndex = ref(-1);

function getLetterPosition(index) {
	const n = props.letters.length;
	const angle = (index / n) * 2 * Math.PI - Math.PI / 2;
	return {
		x: center.value + radius.value * Math.cos(angle),
		y: center.value + radius.value * Math.sin(angle),
	};
}

function getLetterStyle(index) {
	const pos = getLetterPosition(index);
	return {
		left: `${pos.x - letterSize.value / 2}px`,
		top: `${pos.y - letterSize.value / 2}px`,
		width: `${letterSize.value / 10}rem`,
		height: `${letterSize.value / 10}rem`,
	};
}

const pathData = computed(() => {
	if (selectedIndices.value.length < 2) return "";

	const points = selectedIndices.value.map((i) => getLetterPosition(i));
	let d = `M ${points[0].x} ${points[0].y}`;
	for (let i = 1; i < points.length; i++) {
		d += ` L ${points[i].x} ${points[i].y}`;
	}
	return d;
});

const currentWord = computed(() => {
	return selectedIndices.value.map((i) => props.letters[i]).join("");
});

function getEventPos(e) {
	const rect = container.value.getBoundingClientRect();
	const clientX = e.touches ? e.touches[0].clientX : e.clientX;
	const clientY = e.touches ? e.touches[0].clientY : e.clientY;

	const scaleX = size.value / rect.width;
	const scaleY = size.value / rect.height;

	return {
		x: (clientX - rect.left) * scaleX,
		y: (clientY - rect.top) * scaleY,
	};
}

function getLetterAtPos(pos) {
	const hitRadius = letterSize.value / 2 + 10;

	for (let i = 0; i < props.letters.length; i++) {
		const letterPos = getLetterPosition(i);
		const dx = pos.x - letterPos.x;
		const dy = pos.y - letterPos.y;
		const distance = Math.sqrt(dx * dx + dy * dy);

		if (distance < hitRadius) {
			return i;
		}
	}
	return -1;
}

function handleStart(e) {
	e.preventDefault();
	isDragging.value = true;
	selectedIndices.value = [];
	lastExitedIndex.value = -1;

	const pos = getEventPos(e);
	currentPos.value = pos;

	const letterIndex = getLetterAtPos(pos);
	if (letterIndex !== -1) {
		selectedIndices.value.push(letterIndex);
		emit("wordChanging", currentWord.value);
	}
}

function handleMove(e) {
	if (!isDragging.value) return;
	e.preventDefault();

	const pos = getEventPos(e);
	currentPos.value = pos;

	const letterIndex = getLetterAtPos(pos);
	const lastIndex = selectedIndices.value[selectedIndices.value.length - 1];

	if (letterIndex === -1) {
		// Вышли из зоны буквы — запомнить её индекс
		if (lastIndex !== undefined) lastExitedIndex.value = lastIndex;
	} else {
		// Вернулись на ту же букву после выхода из неё — повторный выбор (двойная буква)
		const isReturnToSame = letterIndex === lastIndex && letterIndex === lastExitedIndex.value;
		if (letterIndex !== lastIndex || isReturnToSame) {
			selectedIndices.value.push(letterIndex);
			lastExitedIndex.value = -1;
			emit("wordChanging", currentWord.value);
		}
	}
}

function handleEnd(e) {
	if (!isDragging.value) return;
	e.preventDefault();

	isDragging.value = false;
	currentPos.value = null;

	if (currentWord.value.length >= 2) {
		emit("wordSelected", currentWord.value);
	}

	selectedIndices.value = [];
	emit("wordChanging", "");
}

// Сброс выбора при смене букв
watch(
	() => props.letters,
	() => {
		selectedIndices.value = [];
		currentPos.value = null;
		isDragging.value = false;
		lastExitedIndex.value = -1;
	}
);

onMounted(() => {
	const el = container.value;

	el.addEventListener("mousedown", handleStart);
	el.addEventListener("mousemove", handleMove);
	el.addEventListener("mouseup", handleEnd);
	el.addEventListener("mouseleave", handleEnd);

	el.addEventListener("touchstart", handleStart, { passive: false });
	el.addEventListener("touchmove", handleMove, { passive: false });
	el.addEventListener("touchend", handleEnd, { passive: false });
	el.addEventListener("touchcancel", handleEnd, { passive: false });
});

onUnmounted(() => {
	const el = container.value;
	if (!el) return;

	el.removeEventListener("mousedown", handleStart);
	el.removeEventListener("mousemove", handleMove);
	el.removeEventListener("mouseup", handleEnd);
	el.removeEventListener("mouseleave", handleEnd);

	el.removeEventListener("touchstart", handleStart);
	el.removeEventListener("touchmove", handleMove);
	el.removeEventListener("touchend", handleEnd);
	el.removeEventListener("touchcancel", handleEnd);
});
</script>
<template>
	<div class="wheel-container" ref="container" :style="{ width: size + 'px', height: size + 'px' }">
		<svg class="wheel-svg" :viewBox="`0 0 ${size} ${size}`">
			<!-- Линии между выбранными буквами -->
			<path v-if="pathData" :d="pathData" fill="none" stroke="#4ade80" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="selection-path" />
			<!-- Линия к текущей позиции курсора -->
			<line
				v-if="isDragging && selectedIndices.length > 0 && currentPos"
				:x1="getLetterPosition(selectedIndices[selectedIndices.length - 1]).x"
				:y1="getLetterPosition(selectedIndices[selectedIndices.length - 1]).y"
				:x2="currentPos.x"
				:y2="currentPos.y"
				stroke="#4ade80"
				stroke-width="2"
				stroke-linecap="round"
				opacity="0.5"
			/>
		</svg>

		<!-- Буквы по кругу --> 
		<div v-for="(letter, index) in props.letters" :key="index" class="letter" :class="{ selected: selectedIndices.includes(index) }" :style="getLetterStyle(index)" :data-index="index">
			{{ letter }}
		</div>
	</div>
</template>
<style lang="scss" scoped>
@use "@/assets/scss/base/vars";
.wheel-container {
	position: relative;
	margin: 0 auto;
}

.wheel-svg {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	pointer-events: none;
}

.selection-path {
	filter: drop-shadow(0 0 0.8rem #346707);
}

.letter {
	position: absolute;
	display: flex;
	align-items: center;
	justify-content: center;
	font-family: var(--second-family);
	font-weight: 700;
	font-size: 2.8rem;
	line-height: 110%;
	text-transform: uppercase;
	text-align: center;
	color: var(--text-inverse-100);
	cursor: pointer;
	border-radius: 50%;
	backdrop-filter: blur(18.799999237060547px);
	box-shadow: inset 0 3px 24px 0 rgba(255, 255, 255, 0.14);
	background: rgba(255, 255, 255, 0.01);
	border: 1px solid var(--border-accent-2-1);
	transition:  var(--transition) ease-in;
	@media (max-width: vars.$x480) {
		font-size: 1.4rem;
	}
	&.selected {
		transform: scale(1.15);
		backdrop-filter: blur(18.799999237060547px);
		box-shadow: inset 0 3px 24px 0 rgba(255, 255, 255, 0.14);
		background: var(--fill-accent-2-1);
		color: var(--text-accent-1-100);
	}
}
</style>
